import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import {
  SAPSection,
  SAPChordRow,
  SAPCallout,
  SAPQuote,
  SAPPracticeLadder,
  TwelveBarMap,
} from "@/components/sap/SAPShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { NoteImage, NoteImagePair } from "@/components/mt/NoteImage";

export default function SingAndPlay8() {
  const { lesson, course, accent } = findLesson("sing-and-play", "8");
  return (
    <LessonShell
      course={course}
      courseSlug="sing-and-play"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >

      <AnimatedExplainer
        src="/animations/sing-and-play/SP8-Blues-12-Bar-Animated.mp4"
        caption={`12-bar blues — LH 1-5 shuffle + RH Charleston in G`}
        hint="≈ 26 sec · the blues skeleton"
      />
      {/* ── 1. Big idea ─────────────────────────────────────────────────── */}
      <SAPSection eyebrow="Lesson 8 · Blues" title="The Blues: Two Songs, One Immortal Form">
        <p>
          The 12-bar blues is not a style. It's not a genre. It's a{" "}
          <strong>conversation between your two hands</strong> — and between you and
          everyone who ever played this form before you. Robert Johnson recorded "Sweet
          Home Chicago" in a hotel room in San Antonio in 1936. Eric Clapton recorded
          "I'm Tore Down" in 1994. Both men used the exact same 12-bar skeleton. So have
          thousands of musicians between them.
        </p>
        <p>
          This lesson is your initiation into that tradition. Two songs, same form,
          different textures. By the end, you'll own the 12-bar structure at E, know every
          dominant-seventh chord in the form, and have a left-hand shuffle pattern that
          will serve you for the rest of your musical life.
        </p>
        <SAPCallout label="What makes blues different from gospel and pop" variant="insight">
          Gospel and pop chase resolution — they want to land on the I chord and feel
          "home." Blues lives in the tension. Even the I chord in blues is a dominant
          seventh (E7, not E major) — which technically wants to resolve somewhere else.
          Blues <em>never fully resolves</em>. That is the blues.
        </SAPCallout>
      </SAPSection>

      {/* ── 2. The 12-bar form visualised ──────────────────────────────── */}
      <SAPSection eyebrow="The Architecture" title="The 12-Bar Form — Every Bar Mapped">
        <p>
          Twelve bars. Three chords. One formula. Learn the pattern below and you can play
          in any blues session in the world — no sheet music required. The roman numeral
          labels show exactly which chord function each bar serves.
        </p>

        <TwelveBarMap
          bars={[
            { chord: "E7", fn: "I" },
            { chord: "E7", fn: "I" },
            { chord: "E7", fn: "I" },
            { chord: "E7", fn: "I" },
            { chord: "A7", fn: "IV" },
            { chord: "A7", fn: "IV" },
            { chord: "E7", fn: "I" },
            { chord: "E7", fn: "I" },
            { chord: "B7", fn: "V7" },
            { chord: "A7", fn: "IV" },
            { chord: "E7", fn: "I" },
            { chord: "B7", fn: "V7" },
          ]}
          activeBar={-1}
        />

        <SAPCallout label="The turnaround (bars 11–12)" variant="insight">
          The final two bars are the <strong>turnaround</strong>: E7 then B7. The B7 creates
          maximum tension — it's the V7 of the key — so that when the next 12-bar cycle
          begins on bar 1 (E7), the resolution feels <em>inevitable</em>. A blues that
          doesn't have a strong turnaround lacks propulsion. A blues with a burning
          turnaround sounds like it could go on forever.
        </SAPCallout>

        <RomanNumeralAnalysis
          label="The three chords — E blues"
          caption="All three chords are dominant sevenths. That flat-seven interval is the DNA of the blues sound — it creates unresolved tension in every chord, not just the V."
          chords={[
            { chord: "E7", rn: "I7",  function: "Tonic",    pitches: ["E3","G#3","B3","D4"] },
            { chord: "A7", rn: "IV7", function: "Pre-dominant", pitches: ["A3","C#4","E4","G4"] },
            { chord: "B7", rn: "V7",  function: "Dominant", pitches: ["B3","D#4","F#4","A4"] },
          ]}
        />
      </SAPSection>

      {/* ── 3. Song 1: Sweet Home Chicago ───────────────────────────────── */}
      <SAPSection eyebrow="Song 1 · Robert Johnson (1936)" title="Sweet Home Chicago — The Shuffle Bass">
        <YouTubeEmbed
          videoId="l0PA4odSczU"
          title="Robert Johnson — Sweet Home Chicago (1936 Recording)"
          caption="The original 1936 recording. Listen to the left hand — that rolling, swinging bass line is what we're learning to replicate."
        />

        <p>
          Robert Johnson's playing sounds like two people — a bass player and a guitarist
          occupying the same body. The left hand does everything: roots, fifths, sixths,
          passing tones. At the piano, we isolate the essential element: the{" "}
          <strong>1–5–1–6 shuffle</strong>.
        </p>

        <SAPCallout label="The shuffle pattern (E major)" variant="tip">
          In E major, the shuffle cycle is: <strong>E–B–E–C♯–E–B–E–C♯</strong> per bar.
          That's root (1), fifth (5), root (1), sixth (6) — repeated twice.
          On the piano, starting from the E below middle C: E, B, E, C♯, E, B, E, C♯.
          Play with <strong>swung eighth notes</strong> — the off-beats land slightly late,
          at roughly 2/3 of the beat instead of exactly halfway.
        </SAPCallout>

        <SAPChordRow
          label="The three blues chords — click to hear the dominant 7ths"
          chords={[
            { label: "E7", rn: "I7",  pitches: ["E3","G#3","B3","D4"], fn: "tonic"    },
            { label: "A7", rn: "IV7", pitches: ["A3","C#4","E4","G4"], fn: "subdominant" },
            { label: "B7", rn: "V7",  pitches: ["B3","D#4","F#4","A4"], fn: "dominant" },
          ]}
          caption="The flat 7 in every chord (D in E7, G in A7, A in B7) is the blues note — it keeps every chord permanently unsettled."
        />

        <SAPQuote>
          You can also add a seventh chord to the equation. Instead of just playing your
          usual one, three, five, you can even bring in a seventh chord so you can
          invert it in a way that suits you.
        </SAPQuote>


      </SAPSection>

      {/* ── 4. RH Charleston rhythm ─────────────────────────────────────── */}
      <SAPSection eyebrow="Right Hand Technique" title="The Charleston Rhythm — Anticipation and Attack">
        <p>
          While the LH shuffles steadily, the RH plays a <strong>Charleston rhythm</strong>:
          a sharp chord stab on beat 1, then an anticipated hit on the &-of-2 (slightly
          before beat 3). Together they create the syncopated push-and-pull that defines
          blues piano.
        </p>
        <p>
          Count it: <strong>"1 — and-(2) — 3 — 4 —"</strong>. The hit on the &-of-2
          "leans forward" into beat 3, creating a sense of momentum. That's the Charleston.
          It's the same rhythm used in classic jazz, stride piano, and Chicago blues.
        </p>

        <SAPCallout label="LH and RH: different subdivisions" variant="insight">
          The LH shuffle runs in straight eighth notes (with swing feel). The RH Charleston
          runs in quarter-note dotted rhythms. Two hands moving at <em>different rhythmic
          densities</em> — that independence is the entire blues feel. Don't try to
          synchronise them. Let them breathe against each other.
        </SAPCallout>

        <SAPCallout label="RH voicing for blues impact" variant="tip">
          Instead of full triads in the RH, try <strong>split voicing</strong>: low note
          alone on beat 1, upper two notes (third + fifth) on the Charleston hit. On E7:
          low E on beat 1, then G♯–B (or G♯–D for the 7th colour) on the &-of-2.
          The separation makes the chord sound percussive, not muddy.
        </SAPCallout>
      </SAPSection>

      {/* ── 5. Song 2: I'm Tore Down ────────────────────────────────────── */}
      <SAPSection eyebrow="Song 2 · Eric Clapton (1994)" title="I'm Tore Down — Sophistication Over the Same Form">
        <YouTubeEmbed
          videoId="jHTCabUpLJg"
          title="Eric Clapton — I'm Tore Down"
          caption="Clapton's version (originally by Freddie King, 1961). Same 12-bar form, same E blues — but notice how much more the harmony breathes. That's the sophistication of the arrangement."
        />

        <p>
          "I'm Tore Down" uses the identical 12-bar form. What changes is the{" "}
          <strong>treatment</strong> of that form: more rhythmic space in the bass line,
          more expressive use of the turnaround, and occasional chord substitutions that
          add colour without breaking the structure.
        </p>

        <RomanNumeralAnalysis
          label="I'm Tore Down — same form, richer voicings"
          caption="Notice how adding the flat-7 to every chord transforms the sound. E major becomes E7; A major becomes A7; B major becomes B7. Three small changes; enormous impact on the feel."
          chords={[
            { chord: "E7", rn: "I7",  function: "Tonic",        pitches: ["E3","G#3","B3","D4"] },
            { chord: "E7", rn: "I7",  function: "Tonic",        pitches: ["E3","G#3","B3","D4"] },
            { bar: true },
            { chord: "A7", rn: "IV7", function: "Pre-dominant", pitches: ["A3","C#4","E4","G4"] },
            { chord: "A7", rn: "IV7", function: "Pre-dominant", pitches: ["A3","C#4","E4","G4"] },
            { bar: true },
            { chord: "E7", rn: "I7",  function: "Tonic",        pitches: ["E3","G#3","B3","D4"] },
            { chord: "B7", rn: "V7",  function: "Dominant",     pitches: ["B3","D#4","F#4","A4"] },
            { chord: "A7", rn: "IV7", function: "Pre-dominant", pitches: ["A3","C#4","E4","G4"] },
            { chord: "E7", rn: "I7",  function: "Tonic",        pitches: ["E3","G#3","B3","D4"] },
          ]}
        />

        <SAPCallout label="The turnaround variation" variant="insight">
          For a more sophisticated turnaround on I'm Tore Down, try:
          <strong> E7 → E♭7 → D7 → D♭7 → B7</strong> — a chromatic descent through
          dominant sevenths, each a half-step down, landing on the V7 (B7) with maximum
          tension. This is a bebop-inflected blues move, but it works at any tempo and
          in any blues session.
        </SAPCallout>

        <SAPQuote>
          You need to choose the scale that suits your voice.
        </SAPQuote>


      </SAPSection>

      {/* ── 6. Putting it together ──────────────────────────────────────── */}
      <SAPSection eyebrow="Integration" title="LH Shuffle + RH Charleston + Voice">
        <p>
          The final stage is <strong>triple independence</strong>: left hand shuffling,
          right hand Charleston-ing, and your voice singing the melody on top. This is
          exactly what Robert Johnson and Eric Clapton are doing — three voices,
          one body.
        </p>
        <p>
          The key insight: don't watch your hands. Look up. The eyes-down habit makes you
          tense your shoulders and lose rhythm. Pick a point on the wall. Breathe. Let
          the hands do what they've practised.
        </p>

        <SAPCallout label="Start slow — really slow" variant="warning">
          60 bpm feels embarrassingly slow. Do it anyway. Every student who rushes this
          lesson has to come back and relearn it at half speed. The shuffle groove only
          "locks in" when it's built at a tempo where the hands can relax. Speed is a
          side effect of relaxation — not the other way around.
        </SAPCallout>
      </SAPSection>

      {/* ── 7. Practice ladder ──────────────────────────────────────────── */}
      <SAPSection eyebrow="Your Practice Plan" title="The 40-Minute Blues Initiation">
        <SAPPracticeLadder
          intro="The blues deserves respect. Work through these steps slowly and deliberately — the feeling you're after cannot be rushed."
          rungs={[
            {
              title: "LH shuffle, E chord only",
              minutes: 5,
              detail: "E–B–E–C♯ in swung eighths. Don't move to A or B yet. Just feel the shuffle groove lock in on one chord.",
              accent: "amber",
            },
            {
              title: "LH through the full 12 bars",
              minutes: 8,
              detail: "Play the shuffle through all 12 bars: 4 bars E7, 2 bars A7 (A–E–A–F♯), 2 bars E7, 1 bar B7, 1 bar A7, 1 bar E7, 1 bar B7. No RH yet.",
              accent: "purple",
            },
            {
              title: "RH block chords, beat 1 only",
              minutes: 5,
              detail: "Add one E7 chord in the RH on beat 1 of every bar. Ignore the Charleston for now — just feel where beat 1 is while the LH shuffles.",
              accent: "cyan",
            },
            {
              title: "Add the Charleston &-of-2",
              minutes: 7,
              detail: "Now add the second RH hit on the &-of-2. Count aloud: \"1–and–(2)–and–3–4.\" The &-of-2 hit leans forward. Let it be slightly sharp/aggressive.",
              accent: "emerald",
            },
            {
              title: "Hands together, full 12 bars",
              minutes: 8,
              detail: "Complete 12-bar run with LH shuffle + RH Charleston. Start at 60 bpm. Bring it to 80 only when it feels easy.",
              accent: "rose",
            },
            {
              title: "Sing Sweet Home Chicago",
              minutes: 7,
              detail: "\"Oh baby, don't you want to go…\" Hum the melody first, then add the lyrics. The voice sits naturally over the form once both hands are automatic.",
              accent: "amber",
            },
          ]}
        />
      </SAPSection>


      {/* ── Jason's notes ────────────────────────────────────────────── */}
      <SAPSection eyebrow="Jason's chord charts" title="Handwritten charts — Sweet Home Chicago & I'm Tore Down">
        <p>
          These are Jason's teaching charts for both blues standards. Each chart shows the
          12-bar blues skeleton with chord voicings, bass pattern, and rhythmic notation.
          Click any image to zoom in.
        </p>
        <NoteImagePair
          notes={[
            { src: "/notes/sing-and-play/08-sweet-home-chicago-page1.png", caption: "Sweet Home Chicago — page 1 (E7 blues, I7 IV7 V7)" },
            { src: "/notes/sing-and-play/08-sweet-home-chicago-page2.png", caption: "Sweet Home Chicago — page 2 (charleston rhythm, turnaround)" },
          ]}
        />
        <NoteImagePair
          notes={[
            { src: "/notes/sing-and-play/08-im-tore-down-page1.png", caption: "I'm Tore Down — page 1 (C7 blues, Freddie King style)" },
            { src: "/notes/sing-and-play/08-im-tore-down-page2.png", caption: "I'm Tore Down — page 2 (charleston pattern, bass detail)" },
          ]}
        />
      </SAPSection>
    </LessonShell>
  );
}
